/** Author: Claude Code | Generation: WS payload transforms (pure) | Version: v3.2.181 | Modified: 2025-10-27 */

import { normalizeColor } from '../../utils/colorMapping';

export function transformResourceSpawnPayload(payload: any): any {
  if (!payload) return payload;
  const srcItems: any[] = Array.isArray(payload.newItems)
    ? payload.newItems
    : (Array.isArray(payload.items) ? payload.items : ((payload.itemId && payload.position) ? [payload] : []));
  if (!Array.isArray(srcItems) || srcItems.length === 0) return payload;
  const mapped = srcItems.map((item: any) => {
    if (item && item.position && typeof item.position.x === 'number' && typeof item.position.y === 'number') {
      const out: any = { ...item };
      if (!out.itemId && out.resourceId) out.itemId = out.resourceId;
      try {
        if (out.category && typeof out.category === 'string') out.category = out.category.toLowerCase();
        const meta = out.metadata || {};
        if (out.category === 'weapon') {
          const wt = (out.weaponType || meta.weaponType || '').toString().toUpperCase();
          if (wt) out.weaponType = wt;
        } else if (out.category === 'medical' || out.category === 'consumable') {
          const mt = (out.medicalType || meta.medicalType || '').toString().toLowerCase();
          if (mt) out.medicalType = mt;
          out.category = 'medical';
        }
      } catch {}
      return out;
    }
    return item;
  });
  return { ...payload, newItems: mapped, positionUnit: 'grid' };
}

export function transformAgentSpawnedPayload(payload: any): any {
  if (!payload) return payload;
  let agent: any = payload.agent || null;
  if (!agent) {
    const id = payload.agentId || payload.id;
    const gridPos = payload.position || payload.spawnPosition || payload.pos;
    if (id && gridPos && typeof gridPos.x === 'number' && typeof gridPos.y === 'number') {
      agent = { id, position: gridPos, color: payload.color, health: payload.health };
    }
  }
  if (agent) {
    try {
      const raw = agent.color || payload.color || payload.type || (payload.agent && payload.agent.type) || (payload.agent && payload.agent.color) || payload.slimeType;
      const colorName = normalizeColor(String(raw || ''));
      agent.color = colorName;
    } catch {}
    return { ...payload, agent, agentId: agent.id, positionUnit: 'grid' };
  }
  return payload;
}

export function transformResourceCollectedPayload(payload: any): any {
  const out = { ...payload };
  if (!out.itemId && out.resourceId) out.itemId = out.resourceId;
  out.item = out.item || {};
  const meta = (out.item && out.item.metadata) ? out.item.metadata : (out.metadata || {});
  let category: string | undefined = out.item.category || out.category;
  if (!category && typeof out.type === 'string') {
    const t = String(out.type).toUpperCase();
    if (t === 'WEAPON' || t === 'CONSUMABLE') category = t.toLowerCase();
    else { category = 'weapon'; out.item.type = out.item.type || t; }
  }
  if (category) out.item.category = category.toLowerCase();
  const isWeapon = (out.item.category || '').toLowerCase() === 'weapon' || String(out.type || '').toUpperCase() === 'WEAPON';
  if (isWeapon) {
    const cand = [out.weaponType, out.item.weaponType, meta?.weaponType, out.item.type].map(v => (v == null ? '' : String(v)));
    let chosen = '';
    for (const v of cand) { const up = v.toUpperCase(); if (up && up !== 'WEAPON') { chosen = up; break; } }
    if (chosen) { out.weaponType = chosen; out.item.type = chosen; } else { out.type = 'WEAPON'; }
  }
  const isMedical = (out.item.category || '').toLowerCase() === 'medical' || (out.type && String(out.type).toUpperCase() === 'CONSUMABLE');
  if (isMedical) {
    const rawMed = (out.medicalType || out.item.medicalType || out.item.type || meta.medicalType || '').toString();
    const medType = rawMed ? rawMed.toLowerCase() : '';
    if (medType === 'small' || medType === 'large') {
      out.medicalType = medType; out.item.type = medType; out.item.category = 'medical'; out.type = 'CONSUMABLE';
    } else { out.type = 'CONSUMABLE'; }
  }
  if (!out.type) {
    const cat = String(out.item.category || '').toLowerCase();
    out.type = (cat === 'weapon') ? 'WEAPON' : 'CONSUMABLE';
  }
  return out;
}

export function transformSafeZoneUpdatedPayload(payload: any): any {
  try {
    if (payload && payload.safeZoneInfo) {
      const sz = payload.safeZoneInfo;
      const center = { x: sz.centerX ?? sz.center?.x ?? 0, y: sz.centerY ?? sz.center?.y ?? 0 };
      return { ...payload, center, currentRadius: sz.currentRadius, nextRadius: sz.targetRadius };
    }
  } catch {}
  return payload;
}

export function transformSafeZoneWarningPayload(payload: any): any {
  try {
    if (payload && payload.contraction) {
      const from = payload.contraction.from || {};
      const to = payload.contraction.to || {};
      const center = { x: from.centerX ?? from.center?.x ?? 0, y: from.centerY ?? from.center?.y ?? 0 };
      return { ...payload, center, currentRadius: from.radius, nextRadius: to.radius };
    }
  } catch {}
  return payload;
}

export function transformSafeZoneContractionPayload(payload: any, completed: boolean = false): any {
  try {
    const key = completed ? 'safeZone' : 'contraction';
    const data = payload && payload[key];
    if (data) {
      const center = { x: data.centerX ?? data.center?.x ?? 0, y: data.centerY ?? data.center?.y ?? 0 };
      const radius = data.radius ?? data.to?.radius;
      return completed
        ? { ...payload, safeZone: { center, radius } }
        : { ...payload, safeZone: { center, currentRadius: data.from?.radius, nextRadius: data.to?.radius } };
    }
  } catch {}
  return payload;
}
